"use client";

// Originally ported from relay-protocol packages/relay-widget/src/components/
// RecoverFunds.tsx (v0.1.0); REDESIGNED in place on 2026-07-30 ("Recovery ·
// Recover stuck funds — no addresses, action-first" mockup), then updated to
// upstream e5bff75 (per-forwarder sections + balance composition + up-front
// two-transaction notice). The local "no on-screen addresses" decision is
// KEPT: upstream's version prints the forwarder address and raw tx hashes; a
// bare 0x… string is the single most alarming thing this panel could show, so
// sections here stay address-free (tx links in the composition rows are the
// one exception -- they are the user's own sends, rendered as short links the
// same way the history modal renders them).
//
// The panel is organized PER FORWARDER, not per delivery token. A deployment's
// targets do not map one-to-one onto forwarders: a depositor-kind target (e.g.
// pONEUSD) derives its own, while every swap-kind target shares one. Laying
// out sections per target would put two Recover buttons on a single balance
// the moment two swap-kind targets exist -- both moving the whole balance, the
// second reverting -- the same hazard that rules out splitting per ORIGIN.
// See ../lib/recoveryGroups.ts.
//
// Non-custody invariant, unchanged: the USER signs the permissionless recovery
// tx (they pay BSC gas), and every recovery tx (factory.deploy,
// forwarder.execute/sweepToUser) carries no destination argument -- funds
// always route to the forwarder's immutable recipient. `info.inFlight` is
// advisory only, never used to disable a button.
import { useEffect, useState } from "react";

import { formatUnits } from "../lib/units";
import type { RelayClient } from "../lib/client";
import type { OrderSummary, RecoveryInfo } from "../lib/types";
import type { RecoverySuccessResult, RelayWalletProps } from "../types";
import type { RecoveryAction, UseRecoveryOptions } from "../hooks/useRecovery";
import { useRecovery, useRecoveryActions } from "../hooks/useRecovery";
import type { ForwarderGroup, GroupedDeposit, RecoveryTargetEntry } from "../lib/recoveryGroups";
import { groupForwarders, groupsWithFunds } from "../lib/recoveryGroups";
import { chainDisplayName, explorerTxLink } from "../lib/explorer";
import { TokenIcon } from "./icons";
import "../styles/component.css";

/** BSC -- every forwarder lives here, so an unattributed deposit's arrival tx
 * is a BSC one. */
const BSC_CHAIN_ID = 56;

export interface RecoverFundsProps {
  /** Pre-built client. Takes precedence over apiBaseUrl. */
  client?: RelayClient;
  /** Used to build a client via createRelayClient when `client` isn't passed. */
  apiBaseUrl?: string;
  /** The EVM address to check/recover for -- normally the connected wallet's own address. */
  recipient?: string;
  /**
   * The currently selected CROSS delivery target symbol. Only used when
   * `targets` is NOT supplied, i.e. when this panel fetches for itself: it then
   * reports the one forwarder that target derives. Omitted/undefined selects
   * the configured default.
   */
  target?: string;
  /**
   * Recovery info for EVERY delivery target, pre-fetched by the caller --
   * normally `<RelayDeposit>`/`<RelayRecovery>`, which already make exactly
   * these per-target `getRecovery` calls for the trigger badge. Supplying it
   * puts this panel in per-forwarder mode: it groups the entries by forwarder
   * address and renders a section for each one holding funds, and does NOT
   * fetch anything itself (that would duplicate the caller's request, for one
   * target only).
   *
   * Omitted: the panel fetches the single `target` for itself, exactly as before.
   */
  targets?: readonly RecoveryTargetEntry[];
  /** Loading state for caller-supplied `targets`; ignored by the fetch owner. */
  targetsLoading?: boolean;
  /**
   * The user's orders, used read-only to show WHICH deposits make up each
   * forwarder's balance (origin chain + the user's own send). Only the
   * BSC_FUNDED ones can explain a live balance -- see recoveryGroups.ts.
   * Omitted: balances render without a provenance breakdown.
   */
  orders?: readonly OrderSummary[];
  /**
   * Called after a recovery attempt changes on-chain state, so the owner of
   * `targets` can refetch it. Only meaningful in per-forwarder mode; when this
   * panel fetches for itself it refreshes its own data.
   */
  onRecovered?: () => void;
  /** Called once after the recovery action receipt confirms successfully. */
  onRecoverySuccess?: (result: RecoverySuccessResult) => void;
  /**
   * Injected wallet capabilities (dapp-ui depends on neither wagmi nor viem, so the host
   * supplies these) -- passed straight through to the recovery write path, which
   * signs/switches/reads on BSC. Omitted degrades exactly like a missing capability there:
   * `recover()` fails closed rather than crashing the panel.
   */
  wallet?: RelayWalletProps;
  /**
   * Count of recoverable tokens under OTHER (non-selected) catalog targets.
   *
   * Retained for callers driving this panel one target at a time with their own
   * delivery-token picker. `<RelayDeposit>` no longer passes it and no longer
   * needs to: in per-forwarder mode nothing is hidden behind a selection, so
   * there is no "switch the delivery token to find the rest" left to say.
   */
  otherTargetsRecoverable?: number;
  /**
   * Overrides the pinned factory allowlist `recover()` verifies against before signing
   * (security report L-003/L-008) -- only needed by a self-hosted deployment running its
   * own factories. Defaults to DEFAULT_TRUSTED_FACTORIES.
   */
  trustedFactories?: readonly string[];
  /**
   * Render this panel as its own framed "Recover stuck funds" section (default
   * `true`). `<RelayRecovery>` passes `false`: the modal shell already supplies
   * the accessible title and surface, so another outer card would be redundant.
   */
  showTitle?: boolean;
  /** Called on a 401 from GET /v1/recovery. */
  onUnauthorized?: () => void;
  onError?: (error: Error) => void;
}

function propsToOpts(props: RecoverFundsProps): UseRecoveryOptions {
  return {
    client: props.client,
    apiBaseUrl: props.apiBaseUrl,
    recipient: props.recipient,
    target: props.target,
    // Supplying `targets` means the caller already fetched every target's info;
    // fetching again here would re-request what it just got.
    enabled: props.targets === undefined,
    wallet: props.wallet,
    trustedFactories: props.trustedFactories,
    onUnauthorized: props.onUnauthorized,
    onError: props.onError,
  };
}

const DEFAULT_IN_FLIGHT_REASON = "This balance may already be processing. Recover only if it remains stuck.";

/** The legacy top-level `balance`/`decimals` pair only ever reports BSC USDT (see RecoveryInfo's
 * doc), so that's the symbol its row shows when the backend predates `tokens[]`. */
const LEGACY_SYMBOL = "USDT";

/** Row key for the row built from the legacy top-level `balance` field -- a fixed sentinel rather
 * than an address, since that row's action deliberately passes NO token address (back-compat). */
const PRIMARY_ROW_KEY = "primary";

/** Concise, non-alarming note pointing at the delivery-token picker when a
 * single-target caller's aggregate is higher than what it lists. Singular/plural
 * agree with `n`. */
function otherTargetsNote(n: number): string {
  return `${n} more recoverable under other delivery tokens -- switch the delivery token above to recover ${
    n === 1 ? "it" : "them"
  }.`;
}

/** Keep provider/viem diagnostics out of the primary error line. They can be
 * several paragraphs long and expose calldata that does not help a user decide
 * what to do next; the untouched text remains available in the disclosure. */
function recoveryErrorMessage(error: string): string {
  const normalized = error.toLowerCase();

  if (
    normalized.includes("insufficient funds") ||
    normalized.includes("insufficientfunds") ||
    normalized.includes("exceeds the balance of the account") ||
    normalized.includes("insufficient balance for transfer")
  ) {
    return "Not enough BNB for the network fee. Add BNB to your connected wallet and try again.";
  }

  if (
    normalized.includes("user rejected") ||
    normalized.includes("userrejected") ||
    normalized.includes("user denied") ||
    normalized.includes("user cancelled") ||
    normalized.includes("user canceled") ||
    normalized.includes("action_rejected") ||
    normalized.includes("rejected by user")
  ) {
    return "Transaction cancelled in your wallet.";
  }

  return "Recovery couldn't be completed. Check your wallet and try again.";
}

/** Developer-facing diagnostics live outside the red alert so opening them
 * does not turn the whole error state into one oversized danger panel. */
function TechnicalDetails({ message }: { message: string }) {
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "failed">("idle");

  useEffect(() => {
    if (copyStatus === "idle") return;
    const timer = window.setTimeout(() => setCopyStatus("idle"), 1500);
    return () => window.clearTimeout(timer);
  }, [copyStatus]);

  async function copyDetails() {
    if (typeof navigator === "undefined" || !navigator.clipboard) {
      setCopyStatus("failed");
      return;
    }

    try {
      await navigator.clipboard.writeText(message);
      setCopyStatus("copied");
    } catch {
      setCopyStatus("failed");
    }
  }

  const copyLabel =
    copyStatus === "copied"
      ? "Copied technical details"
      : copyStatus === "failed"
        ? "Copy failed"
        : "Copy technical details";

  return (
    <details className="rd-error-details">
      <summary>View technical details</summary>
      <div className="rd-error-log">
        <button
          type="button"
          className="rd-error-copy"
          onClick={() => void copyDetails()}
          aria-label={copyLabel}
          aria-live="polite"
          title={copyLabel}
        >
          {copyStatus === "copied" ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="m5 12 4 4L19 6"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="8" y="8" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <path
                d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          )}
        </button>
        <code>{message}</code>
      </div>
    </details>
  );
}

/** Every smallest-unit amount off the API is a base-10 string; a malformed one degrades to 0n
 * rather than throwing the whole panel away. All comparisons/arithmetic below stay in BigInt. */
function toBigInt(value: string | undefined): bigint {
  if (!value) return 0n;
  try {
    return BigInt(value);
  } catch {
    return 0n;
  }
}

/** Human-readable amount (floor semantics -- formatUnits is a pure digit shift). Display only:
 * never fed back into any computation. */
function human(value: bigint, decimals: number): string {
  try {
    return formatUnits(value, decimals);
  } catch {
    return value.toString();
  }
}

/** Truncates a 0x hash for inline display. Defensive fallback for short strings. */
function shortHash(hash: string): string {
  return hash.length > 14 ? `${hash.slice(0, 8)}...${hash.slice(-4)}` : hash;
}

/**
 * One on-screen asset row. Derived entirely from `RecoveryInfo` -- `action` is the recover()
 * call this row's button makes (undefined = nothing this row can do on its own), and `shortfall`
 * is how much more of the same asset the bridge minimum still needs.
 */
interface RecoveryRow {
  key: string;
  symbol: string;
  decimals: number;
  balance: bigint;
  action?: { kind: RecoveryAction; tokenAddress?: string };
  /** Set only when the row is NOT recoverable because it sits below the bridge minimum. */
  shortfall?: bigint;
  /** The bridge minimum, in this row's own units -- only meaningful alongside `shortfall`. */
  minimum?: bigint;
}

/**
 * Builds one forwarder's row list, newest semantics first:
 *
 *   * `tokens[]` (Task 6/7 backend) is the source of truth when present -- one row per token
 *     holding a nonzero balance, INCLUDING the ones the server flags un-recoverable (those are
 *     exactly the below-minimum leftovers the redesign has to explain rather than hide);
 *   * a top-level `suggestedAction: "execute"` still gets its own row when no `tokens[]` entry
 *     already carries the execute action -- that's the normal bridge-completion asset;
 *   * with no `tokens[]` at all (older backend) the legacy `balance`/`decimals` pair is the only
 *     row, and its action passes no token address -- byte-identical to pre-Task-7 behavior.
 *
 * Zero-balance rows are omitted everywhere: there is nothing to say about them.
 */
function buildRows(info: RecoveryInfo): RecoveryRow[] {
  const minimum = toBigInt(info.bridgeMinWei);
  const rows: RecoveryRow[] = [];

  for (const token of info.tokens ?? []) {
    const balance = toBigInt(token.balance);
    if (balance <= 0n) continue;
    if (token.suggestedAction === "execute") {
      rows.push({
        key: token.address,
        symbol: token.symbol,
        decimals: token.decimals,
        balance,
        action: { kind: "execute" },
      });
      continue;
    }
    if (token.recoverable) {
      rows.push({
        key: token.address,
        symbol: token.symbol,
        decimals: token.decimals,
        balance,
        action: { kind: "sweep", tokenAddress: token.address },
      });
      continue;
    }
    rows.push({
      key: token.address,
      symbol: token.symbol,
      decimals: token.decimals,
      balance,
      ...(minimum > balance ? { shortfall: minimum - balance, minimum } : {}),
    });
  }

  const primaryBalance = toBigInt(info.balance);
  const hasExecuteRow = rows.some((r) => r.action?.kind === "execute");
  if (primaryBalance > 0n && info.suggestedAction === "execute" && !hasExecuteRow) {
    // The bridge-completion asset the top-level fields describe. Prepended: it is the asset the
    // user actually deposited, so it leads the list ahead of any misdeposited leftovers.
    rows.unshift({
      key: PRIMARY_ROW_KEY,
      symbol: LEGACY_SYMBOL,
      decimals: info.decimals,
      balance: primaryBalance,
      action: { kind: "execute" },
    });
  } else if (rows.length === 0 && primaryBalance > 0n && info.suggestedAction !== "none") {
    // Older backend with no `tokens[]`: the legacy single-asset row. `sweep` with no token
    // address, exactly as the pre-Task-7 generic button did.
    rows.push({
      key: PRIMARY_ROW_KEY,
      symbol: LEGACY_SYMBOL,
      decimals: info.decimals,
      balance: primaryBalance,
      action: { kind: "sweep" },
    });
  }

  return rows;
}

/** One provenance row: which chain a deposit came from, how much arrived, and a
 * link to the user's own send. Read-only by design -- there is no per-origin
 * action to offer (see ForwarderGroup.deposits). */
function DepositRow({ deposit, decimals }: { deposit: GroupedDeposit; decimals: number }) {
  // An attributed deposit links the user's ORIGIN-chain send; an unattributed one
  // has only its BSC arrival, which is still worth linking -- and is permanently
  // correct for a direct BSC deposit, which has no cross-chain leg at all.
  const chainId = deposit.originChainId ?? BSC_CHAIN_ID;
  const txHash = deposit.originTxHash || deposit.txHash;
  const link = txHash ? explorerTxLink(chainId, txHash) : undefined;
  return (
    <div className="rd-history-step" data-testid="recovery-deposit-row">
      <span className="rd-history-step-dot rd-history-step-dot-done" />
      <span className="rd-history-step-chain">{chainDisplayName(chainId)}</span>
      <span className="rd-history-step-amount">{human(toBigInt(deposit.amount), decimals)}</span>
      <span className="rd-history-step-tx">
        {link ? (
          <a href={link.url} target="_blank" rel="noreferrer" title={txHash}>
            {shortHash(txHash)}
          </a>
        ) : txHash ? (
          <span title={txHash}>{shortHash(txHash)}</span>
        ) : null}
      </span>
    </div>
  );
}

interface ForwarderSectionProps {
  group: ForwarderGroup;
  /** Whether to name which delivery tokens this forwarder serves. Worth saying in
   * two cases: several forwarders are listed (so the user can tell which is
   * which), or this one serves several targets (so a user who picked one of them
   * understands why a single entry covers it). Suppressed only in the plain
   * one-forwarder-one-target case, where it would be noise. */
  showTargetLabel: boolean;
  busy: boolean;
  state: ReturnType<ReturnType<typeof useRecoveryActions>["stateFor"]>;
  onRecover: (row: RecoveryRow) => void;
  /** Which row's button was clicked (panel-scoped key), so the in-progress
   * spinner replaces THAT row's button only. */
  actingRowKey?: string;
  rowKeyOf: (row: RecoveryRow) => string;
}

function ForwarderSection({
  group,
  showTargetLabel,
  busy,
  state,
  onRecover,
  actingRowKey,
  rowKeyOf,
}: ForwarderSectionProps) {
  const { info } = group;
  const rows = buildRows(info);
  const balanceValue = toBigInt(info.balance);

  /** Never sums across symbols: a single row shows its own amount, several show a count. */
  const headline = rows.length === 1 ? `${human(rows[0].balance, rows[0].decimals)}` : `${rows.length} assets`;

  // The listed deposits can add up to LESS than the balance: a deposit seen
  // on-chain but not yet swept into an order has no order to be listed under,
  // and residual dust from an order already written off has none either.
  // Saying so is the whole difference between a breakdown and a claim.
  const depositsExplainAll = group.deposits.length > 0 && group.depositsTotal >= balanceValue;
  const unexplained = balanceValue > group.depositsTotal ? balanceValue - group.depositsTotal : 0n;

  const running = state.step === "switching" || state.step === "deploying" || state.step === "recovering";
  const actingRow = rows.find((row) => rowKeyOf(row) === actingRowKey);
  const actingAction = actingRow?.action?.kind;

  /** The wallet-confirmation progress for the acting row, in the user's terms. */
  function stepText(): string {
    if (state.step === "switching") return "Switching to BNB Chain…";
    if (state.step === "deploying") return "Confirm in wallet… (1/2 — preparing your deposit address)";
    const action = actingAction === "execute" ? "continuing ONEchain delivery" : "returning funds";
    return state.deployTxHash ? `Confirm in wallet… (2/2 — ${action})` : `Confirm in wallet… (${action})`;
  }

  return (
    <div className="rd-recover-group" data-testid="forwarder-section">
      {showTargetLabel && (
        <p className="rd-recover-group-label">
          {/* Plural because several delivery tokens genuinely share one forwarder
              (every swap-kind target does) -- and when they do, this is ONE
              balance, recovered once, not one per token. */}
          Delivery target · <b>{group.targets.join(", ")}</b>
        </p>
      )}

      <div className="rd-recover-sum">
        <span className="rd-recover-sum-label">Funds awaiting processing</span>
        <span
          className="rd-recover-sum-value"
          title={rows.length === 1 ? `${headline} ${rows[0].symbol}` : headline}
        >
          {headline}
          {rows.length === 1 && (
            <>
              {" "}
              <span className="rd-recover-sum-unit">{rows[0].symbol}</span>
            </>
          )}
        </span>
      </div>

      {group.deposits.length > 0 && (
        <details className="rd-recover-made-of">
          <summary className="rd-recover-made-of-label">
            Deposit history · {group.deposits.length} {group.deposits.length === 1 ? "transfer" : "transfers"}
          </summary>
          <div className="rd-recover-made-of-body">
            {group.deposits.map((d) => (
              <DepositRow key={`${d.txHash}-${d.blockNumber}`} deposit={d} decimals={info.decimals} />
            ))}
            {!depositsExplainAll && (
              <p className="rd-token-row-status" data-testid="deposits-incomplete-note">
                {/* Named precisely: the rows above are what we can ATTRIBUTE, and the
                    gap is real money that a recovery will also move. */}
                These transfers explain {human(group.depositsTotal, info.decimals)} of{" "}
                {human(balanceValue, info.decimals)} {LEGACY_SYMBOL}. The remaining{" "}
                {human(unexplained, info.decimals)} {LEGACY_SYMBOL} is still included when you act.
              </p>
            )}
          </div>
        </details>
      )}

      <div className="rd-token-list">
        {rows.map((row) => {
          const key = rowKeyOf(row);
          const acting = running && actingRowKey === key;
          return (
            <div key={row.key} className="rd-token-row" data-token-symbol={row.symbol}>
              <TokenIcon symbol={row.symbol} size={26} />
              <div className="rd-token-row-info">
                {rows.length > 1 && (
                  <span className="rd-token-row-amount">
                    {human(row.balance, row.decimals)} <span className="rd-token-row-symbol">{row.symbol}</span>
                  </span>
                )}
                {acting ? (
                  <span className="rd-token-row-step" role="status" aria-live="polite">
                    {stepText()}
                  </span>
                ) : row.shortfall !== undefined && row.minimum !== undefined ? (
                  <span className="rd-token-row-warn">
                    Needs <b>{human(row.shortfall, row.decimals)} {row.symbol}</b> more to reach the{" "}
                    {human(row.minimum, row.decimals)} {row.symbol} minimum. Add it through{" "}
                    <b>Deposit to CROSS</b> and both amounts will process together.
                  </span>
                ) : row.action ? (
                  <span className="rd-token-row-status">
                    {row.action.kind === "execute"
                      ? "Ready to continue delivery to ONEchain"
                      : "Ready to return to your BNB Chain wallet"}
                  </span>
                ) : (
                  <span className="rd-token-row-status">Waiting for processing</span>
                )}
              </div>
              {acting ? (
                <span className="rd-spinner rd-token-row-spinner" />
              ) : (
                row.action && (
                  <button
                    type="button"
                    className="rd-button-primary rd-token-row-action"
                    onClick={() => onRecover(row)}
                    disabled={busy}
                  >
                    {row.action.kind === "execute" ? "Continue delivery" : "Return to wallet"}
                  </button>
                )
              )}
            </div>
          );
        })}
      </div>

      {/* The two-transaction case surprised a user, so it is stated BEFORE they
          commit rather than discovered at the second wallet prompt (upstream
          e5bff75). An ERC-20 balance can sit at a CREATE2 address with no code,
          but sweepToUser is a contract function, so the forwarder has to exist
          first. */}
      {!info.isDeployed && state.step === "idle" && (
        <div className="rd-callout rd-callout-warning" data-testid="two-tx-notice">
          The first action from this address needs <b>two wallet confirmations</b>: prepare the address,
          then move the funds. Later actions need one.
        </div>
      )}

      {/* Advisory only, never a blocker (see this file's header). */}
      {info.inFlight && (
        <p className="rd-muted rd-token-row-status">{info.inFlightReason || DEFAULT_IN_FLIGHT_REASON}</p>
      )}

      {state.step === "done" && (
        <div className="rd-callout rd-callout-success" role="status" aria-live="polite">
          {actingAction === "execute"
            ? "ONEchain delivery restarted."
            : "Funds returned to your connected BNB Chain wallet."}
        </div>
      )}

      {state.step === "error" && state.error && (
        <>
          <div className="rd-callout rd-callout-danger" role="alert">
            {recoveryErrorMessage(state.error)}
          </div>
          <TechnicalDetails message={state.error} />
        </>
      )}
    </div>
  );
}

export function RecoverFunds(props: RecoverFundsProps) {
  // Own fetch, used only when the caller did not supply `targets` (see
  // propsToOpts' `enabled`). Its `recover`/`recoveryState` are not used here --
  // the sections drive the shared write path below so each forwarder gets its
  // own button and its own progress.
  const { info, loading, error, refresh } = useRecovery(propsToOpts(props));
  const isLoading = loading || (props.targets !== undefined && props.targetsLoading === true);

  const actions = useRecoveryActions({
    recipient: props.recipient,
    wallet: props.wallet,
    trustedFactories: props.trustedFactories,
    onError: props.onError,
    onRecoverySuccess: props.onRecoverySuccess,
    // Refresh whichever side owns the data: the caller's per-target fetch in
    // per-forwarder mode, this panel's own otherwise.
    onChanged: props.targets !== undefined ? props.onRecovered : refresh,
  });

  /** Which (forwarder, row) button was clicked, so the in-progress spinner + step
   * text replace THAT row's button (and only that one). Presentation state only. */
  const [actingRowKey, setActingRowKey] = useState<string>();

  // Entries to group. In per-forwarder mode they come from the caller; otherwise
  // this panel's single fetched target stands alone. `isDefault` matters only for
  // matching orders (which report "" for the default target), and a lone
  // self-fetched entry is treated as the default so an order with target "" still
  // matches it.
  const entries: RecoveryTargetEntry[] =
    props.targets !== undefined
      ? [...props.targets]
      : info
        ? [{ symbol: props.target ?? "", isDefault: true, info }]
        : [];

  const groups = groupsWithFunds(groupForwarders(entries, props.orders ?? []));
  const hasFunds = groups.length > 0;
  const refreshFunds = props.targets !== undefined ? props.onRecovered : refresh;

  // The CONNECTED wallet's BNB balance -- recovery txs run on BSC and the gas
  // comes from the signer (walletAddress), not the recipient. Fetched only
  // when there is something to recover and the host injected `getBalance`;
  // failure (or a missing capability) just leaves the note without a figure.
  // "Insufficient funds" at the wallet prompt was this panel's most confusing
  // dead end -- the user sees USDT waiting and no reason a click should fail.
  const walletAddress = props.wallet?.walletAddress;
  const getBalance = props.wallet?.getBalance;
  const [gasBalance, setGasBalance] = useState<{ value: bigint; decimals: number } | undefined>();
  useEffect(() => {
    if (!hasFunds || !getBalance || !walletAddress) {
      setGasBalance(undefined);
      return;
    }
    let cancelled = false;
    getBalance({ chainId: 56, address: walletAddress as `0x${string}` })
      .then((b) => {
        if (!cancelled) setGasBalance({ value: b.value, decimals: b.decimals });
      })
      .catch(() => {
        if (!cancelled) setGasBalance(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [hasFunds, getBalance, walletAddress]);

  return (
    <section
      className={(props.showTitle ?? true) ? "rd-card" : "rd-stack"}
      data-rd-recover-funds=""
    >
      {(props.showTitle ?? true) && (
        <div className="rd-card-header">
          <h2 className="rd-card-title">Recover stuck funds</h2>
        </div>
      )}

      {/* Only meaningful for single-target embedders -- <RelayDeposit>/<RelayRecovery>
          run per-forwarder mode and no longer pass it (nothing is hidden behind the
          selection any more). */}
      {typeof props.otherTargetsRecoverable === "number" && props.otherTargetsRecoverable > 0 && (
        <div className="rd-callout rd-callout-muted" data-testid="other-targets-recoverable-note">
          {otherTargetsNote(props.otherTargetsRecoverable)}
        </div>
      )}

      {isLoading && (
        <p className="rd-muted" role="status" aria-live="polite">
          <span className="rd-spinner" /> Checking for recoverable funds…
        </p>
      )}

      {error && (
        <>
          <div className="rd-callout rd-callout-danger" role="alert">
            Couldn&apos;t check recoverable funds. Please try again.
          </div>
          <TechnicalDetails message={error.message} />
        </>
      )}

      {!isLoading && !error && !hasFunds && (
        <div className="rd-recover-empty" role="status" data-testid="rd-recover-empty-state">
          <span className="rd-recover-empty-icon" aria-hidden="true">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" focusable="false">
              <path
                d="M5 11.5V8.8A2.8 2.8 0 0 1 7.8 6h8.4A2.8 2.8 0 0 1 19 8.8v6.4a2.8 2.8 0 0 1-2.8 2.8H7.8A2.8 2.8 0 0 1 5 15.2v-3.7Z"
                stroke="currentColor"
                strokeWidth="1.7"
              />
              <path d="M5 10h14" stroke="currentColor" strokeWidth="1.7" />
              <path
                d="m9.2 14 1.7 1.7 3.9-4"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <h3 className="rd-recover-sum-value rd-recover-sum-empty rd-recover-empty-title">
            Nothing to recover
          </h3>
          <p className="rd-recover-empty-copy">
            No funds are waiting at your deposit addresses. If funds become stuck, they will appear here.
          </p>
          {refreshFunds && (
            <button
              type="button"
              className="rd-button-secondary rd-recover-empty-refresh"
              onClick={refreshFunds}
              data-track="refresh_recovery"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M20 7v5h-5M4 17v-5h5M6.1 9A7 7 0 0 1 18.6 7M5.4 17A7 7 0 0 0 17.9 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Check again
            </button>
          )}
        </div>
      )}

      {!isLoading &&
        !error &&
        groups.map((group) => (
          <ForwarderSection
            key={group.forwarder.toLowerCase()}
            group={group}
            showTargetLabel={groups.length > 1 || group.targets.length > 1}
            busy={actions.busy}
            state={actions.stateFor(group.forwarder)}
            actingRowKey={actingRowKey}
            rowKeyOf={(row) => `${group.forwarder.toLowerCase()}:${row.key}`}
            onRecover={(row) => {
              if (!row.action) return;
              setActingRowKey(`${group.forwarder.toLowerCase()}:${row.key}`);
              void actions.recover(group.info, row.action.kind, row.action.tokenAddress);
            }}
          />
        ))}

      {/* Recovery signs on BSC and the gas comes out of the CONNECTED wallet's
          BNB -- said up front, with the wallet's live balance when the host can
          read it, because "insufficient funds" at the wallet prompt is this
          panel's most confusing dead end otherwise. */}
      {!isLoading && !error && hasFunds && (
        <div className="rd-callout rd-callout-muted" data-testid="rd-recover-gas-note">
          You submit this transaction from your connected wallet and pay the BNB Chain network fee in BNB.
          Available balance:
          {gasBalance && (
            <>
              {" "}
              <b>{human(gasBalance.value, gasBalance.decimals)} BNB</b>
            </>
          )}
          {!gasBalance && " unavailable"}.
        </div>
      )}

      {/* The one thing worth saying about WHERE the money goes -- it replaces the forwarder
          address this panel used to print. True by construction: no recovery tx takes a
          destination argument. */}
      {!isLoading && !error && hasFunds && (
        <p className="rd-recover-foot">
          Delivery continues to ONEchain; returned funds go to your connected BNB Chain wallet.
        </p>
      )}
    </section>
  );
}
